/**
 * The Strip Card board (ISSUE-1288), after v1's strip_game_tab.dart: a fixed
 * members column with the ten rows of the selected strip, beside a row of
 * strip columns (S1 to S4) that scrolls sideways inside the board on a phone,
 * never the page.
 *
 * Each strip column is its header (label and Open or Locked, plus Manage for
 * an admin) and ten digit cells: a lock until the strip is locked, then the
 * drawn digit in tabular figures, filled solid when the row has a winner.
 * The selected strip has a solid border and `aria-pressed`, so the selection
 * reads without color. Everything shown comes from `/strips/card-get`.
 */
import { cn, FOCUS_RING, REDUCED_MOTION_SAFE, Spinner } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { PlusIcon } from "../../icons/glyphs";
import {
  rowHolderName,
  type typeStripCardData,
  type typeStripRowView,
  type typeStripView,
} from "./stripBoardModel";
import {
  STRIP_ADD_STRIP_LABEL,
  STRIP_BOARD_LABEL,
  STRIP_MANAGE_LABEL,
  STRIP_OPEN_ROW_LABEL,
  STRIP_STATE_LOCKED,
  STRIP_STATE_OPEN,
  STRIP_TAKE_ROW_LABEL,
  STRIP_USERS_COLUMN_LABEL,
  stripDigitAriaLabel,
  stripManageAriaLabel,
  stripRowAriaLabel,
  stripSelectAriaLabel,
} from "./stripCardCopy";
import { StripLockGlyph, StripSlidersGlyph } from "./stripGlyphs";
import type { typeStripBusy } from "./useStripCardGame";

export type typeStripBoardProps = {
  card: typeStripCardData;
  selectedStripNumber: number;
  busy: typeStripBusy;
  onSelectStrip: (stripNumber: number) => void;
  onMemberCellTap: (strip: typeStripView, row: typeStripRowView) => void;
  onDigitCellTap: (strip: typeStripView, row: typeStripRowView) => void;
  onManageStrip: (strip: typeStripView) => void;
  onAddStrip: () => void;
};

const CELL_MOTION = cn("transition-colors duration-200", REDUCED_MOTION_SAFE);

export const StripBoard = (props: typeStripBoardProps): ReactNode => {
  const { card, selectedStripNumber, busy } = props;
  const canManage = card.viewer.canManage;
  const selectedStrip =
    card.strips.find((strip) => strip.stripNumber === selectedStripNumber) ??
    card.strips[0];
  if (selectedStrip === undefined) return null;
  const isCanceled = card.card.isCanceled;
  const showAddStrip = canManage && card.card.canAddStrip && !isCanceled;
  const isAddingStrip = busy?.kind === "addStrip";

  return (
    <div
      role="group"
      aria-label={STRIP_BOARD_LABEL}
      data-testid="strip-board"
      className="flex gap-2 rounded-md border border-line bg-surface p-2"
    >
      <div
        className="flex w-32 shrink-0 flex-col gap-1 md:w-48"
        data-testid="strip-members-column"
      >
        <div className="flex h-12 items-end px-2 pb-1 text-caption text-ink-secondary">
          {STRIP_USERS_COLUMN_LABEL}
        </div>
        {canManage ? <div aria-hidden="true" className="h-11" /> : null}
        {selectedStrip.rows.map((row) => (
          <MemberCell
            key={row.row}
            strip={selectedStrip}
            row={row}
            isCanceled={isCanceled}
            isBusy={
              busy?.kind === "row" &&
              busy.stripNumber === selectedStrip.stripNumber &&
              busy.row === row.row
            }
            onTap={() => {
              props.onMemberCellTap(selectedStrip, row);
            }}
          />
        ))}
      </div>

      <div className="min-w-0 flex-1 overflow-x-auto">
        <div className="flex w-max gap-1">
          {card.strips.map((strip) => {
            const isSelected = strip.stripNumber === selectedStrip.stripNumber;
            return (
              <div
                key={strip.stripNumber}
                data-testid={`strip-column-${String(strip.stripNumber)}`}
                data-selected={isSelected ? "true" : "false"}
                className={cn(
                  "flex w-14 flex-col gap-1 rounded-md border-2",
                  CELL_MOTION,
                  isSelected
                    ? "border-primary bg-primary-tint"
                    : "border-transparent",
                )}
              >
                <button
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={stripSelectAriaLabel(strip.label)}
                  data-testid={`strip-select-${String(strip.stripNumber)}`}
                  onClick={() => {
                    props.onSelectStrip(strip.stripNumber);
                  }}
                  className={cn(
                    "flex h-12 flex-col items-center justify-center gap-1 rounded-sm",
                    FOCUS_RING,
                  )}
                >
                  <span className="text-body-strong text-ink">
                    {strip.label}
                  </span>
                  <span
                    className={cn(
                      "text-small",
                      strip.state === "locked"
                        ? "text-ink-secondary"
                        : "text-primary",
                    )}
                  >
                    {strip.state === "locked"
                      ? STRIP_STATE_LOCKED
                      : STRIP_STATE_OPEN}
                  </span>
                </button>
                {canManage ? (
                  <button
                    type="button"
                    aria-label={stripManageAriaLabel(strip.label)}
                    title={STRIP_MANAGE_LABEL}
                    data-testid={`strip-manage-${String(strip.stripNumber)}`}
                    onClick={() => {
                      props.onManageStrip(strip);
                    }}
                    className={cn(
                      "flex h-11 items-center justify-center rounded-sm border border-line bg-surface text-ink hover:bg-surface-sunken",
                      FOCUS_RING,
                    )}
                  >
                    {busy !== null &&
                    (busy.kind === "lock" ||
                      busy.kind === "unlock" ||
                      busy.kind === "copy") &&
                    busy.stripNumber === strip.stripNumber ? (
                      <Spinner className="h-icon-sm w-icon-sm" />
                    ) : (
                      <StripSlidersGlyph />
                    )}
                  </button>
                ) : null}
                {strip.rows.map((row) => (
                  <DigitCell
                    key={row.row}
                    strip={strip}
                    row={row}
                    isCanceled={isCanceled}
                    onTap={() => {
                      props.onDigitCellTap(strip, row);
                    }}
                  />
                ))}
              </div>
            );
          })}
          {showAddStrip ? (
            <div className="flex w-14 flex-col">
              <button
                type="button"
                aria-label={STRIP_ADD_STRIP_LABEL}
                aria-busy={isAddingStrip || undefined}
                data-testid="strip-add"
                onClick={props.onAddStrip}
                className={cn(
                  "flex h-12 items-center justify-center rounded-sm border-2 border-dashed border-line-strong text-primary hover:bg-primary-tint",
                  FOCUS_RING,
                )}
              >
                {isAddingStrip ? (
                  <Spinner className="h-icon-sm w-icon-sm" />
                ) : (
                  <PlusIcon size="md" />
                )}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const MemberCell = (props: {
  strip: typeStripView;
  row: typeStripRowView;
  isCanceled: boolean;
  isBusy: boolean;
  onTap: () => void;
}): ReactNode => {
  const { strip, row } = props;
  const holder = rowHolderName(row);
  const canTake =
    holder === null &&
    strip.canClaim &&
    !props.isCanceled &&
    strip.state === "open";
  return (
    <button
      type="button"
      data-testid={`strip-member-${String(row.row)}`}
      data-mine={row.isMine ? "true" : "false"}
      aria-label={stripRowAriaLabel({
        rowLabel: row.rowLabel,
        stripLabel: strip.label,
        holder,
      })}
      aria-busy={props.isBusy || undefined}
      onClick={props.onTap}
      className={cn(
        "flex h-12 min-w-0 items-center gap-2 rounded-sm border px-2 text-left",
        CELL_MOTION,
        FOCUS_RING,
        row.isMine
          ? "border-primary bg-primary-tint"
          : "border-line bg-surface hover:bg-surface-sunken",
      )}
    >
      <span className="w-4 shrink-0 text-small text-ink-tertiary tabular-nums">
        {row.rowLabel}
      </span>
      {holder === null ? (
        <span
          className={cn(
            "truncate text-caption",
            canTake ? "text-primary" : "text-ink-tertiary",
          )}
        >
          {canTake ? STRIP_TAKE_ROW_LABEL : STRIP_OPEN_ROW_LABEL}
        </span>
      ) : (
        <>
          <MemberMark row={row} />
          <span className="min-w-0 truncate text-caption text-ink">
            {holder}
          </span>
        </>
      )}
      {props.isBusy ? (
        <Spinner className="ml-auto h-icon-sm w-icon-sm shrink-0" />
      ) : null}
    </button>
  );
};

const MemberMark = ({ row }: { row: typeStripRowView }): ReactNode => {
  if (typeof row.avatarUrl === "string" && row.avatarUrl !== "") {
    return (
      <img
        src={row.avatarUrl}
        alt=""
        className="hidden h-8 w-8 shrink-0 rounded-full object-cover md:block"
      />
    );
  }
  const initials = row.initials ?? "";
  if (initials === "") return null;
  return (
    <span
      aria-hidden="true"
      className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-small text-ink-secondary md:flex"
    >
      {initials}
    </span>
  );
};

const DigitCell = (props: {
  strip: typeStripView;
  row: typeStripRowView;
  isCanceled: boolean;
  onTap: () => void;
}): ReactNode => {
  const { strip, row } = props;
  const digit = row.digit ?? null;
  const hasWinner = !props.isCanceled && row.winners.length > 0;
  return (
    <button
      type="button"
      data-testid={`strip-digit-${String(strip.stripNumber)}-${String(row.row)}`}
      data-winner={hasWinner ? "true" : "false"}
      aria-label={stripDigitAriaLabel({
        rowLabel: row.rowLabel,
        stripLabel: strip.label,
        digit,
        winnerCount: props.isCanceled ? 0 : row.winners.length,
      })}
      onClick={props.onTap}
      className={cn(
        "flex h-12 items-center justify-center rounded-sm",
        CELL_MOTION,
        FOCUS_RING,
        hasWinner
          ? "bg-primary-solid text-on-primary"
          : "bg-surface-sunken text-ink",
      )}
    >
      {digit === null ? (
        <StripLockGlyph
          className={hasWinner ? "text-on-primary" : "text-ink-tertiary"}
        />
      ) : (
        <span className="text-body-strong tabular-nums">{digit}</span>
      )}
    </button>
  );
};
